import { computed, inject, Injectable } from '@angular/core';
import { CartV2Store } from './cart-v2.store';
import { instanceOfPortalProduct, isEcashItem, isFreeItem, isMerchantItem, isPointsItem } from './cart.helper';
import { MemberQuery } from '../../../account/member/member.query';
import { MarketplaceItemV2 } from '../../marketplace/marketplace-v2/models/marketplace-item-v2.model';
import { Cart } from '../models/cart.model';
import { PortalProduct } from '../../portal-products/models/portal-product.model';

@Injectable({
  providedIn: 'root',
})
export class CartV2Query {
  private cartStore = inject(CartV2Store);
  private memberQuery = inject(MemberQuery);
  readonly items = computed(() => this.cartStore.items().filter((entity) => this.isItemFromCurrentMember(entity)));

  readonly summary = computed(() => {
    const merchantItems = this.items()
      .filter((entity) => isMerchantItem(entity))
      .map(this.mapAsMarketplaceItemCart);
    const ecashItems = this.items()
      .filter((entity) => isEcashItem(entity))
      .map(this.mapAsMarketplaceItemCart);
    const pointsItems = this.items()
      .filter((entity) => isPointsItem(entity))
      .map(this.mapAsMarketplaceItemCart);
    const productItems = this.items()
      .filter((entity) => instanceOfPortalProduct(entity.productData))
      .map(this.mapAsPortalProductCart);
    const freeItems = this.items()
      .filter((entity) => isFreeItem(entity))
      .map(this.mapAsMarketplaceItemCart);
    return {
      merchantItems: {
        items: merchantItems,
        total: merchantItems.reduce((acc, item) => acc + item.purchaseQuantity * item.productData.MerchantValue, 0),
      },
      ecashItems: {
        items: ecashItems,
        total: ecashItems.reduce((acc, item) => acc + item.purchaseQuantity * item.productData.EcashValue, 0),
      },
      pointsItems: {
        items: pointsItems,
        total: pointsItems.reduce((acc, item) => acc + item.purchaseQuantity * item.productData.PointValue, 0),
      },
      productItems: {
        items: productItems,
        total: productItems.reduce((acc, item) => acc + item.purchaseQuantity * item.productData.MerchantPrice, 0),
      },
      freeItems: {
        items: freeItems,
        total: 0,
      },
      totalQuantity: this.items().reduce((acc, item) => acc + item.purchaseQuantity, 0),
    };
  });

  private isItemFromCurrentMember = (entity: Cart) => entity.memberId == this.memberQuery.getUserContext().memberId;
  private mapAsMarketplaceItemCart = (entity: Cart) => ({ ...entity, productData: entity.productData as MarketplaceItemV2 });
  private mapAsPortalProductCart = (entity: Cart) => ({ ...entity, productData: entity.productData as PortalProduct });
}
